import $i18n from 'panda-i18n';
import React, { forwardRef, useEffect, useRef, useState, useCallback, } from 'react';
import isString from 'lodash/isString';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';
import { CnLoading } from '../cn-loading/loading';
import { CnIcon } from '../cn-icon';
import { CnDialog } from '../cn-dialog';
import { useValue } from '../../utils/use-value';
const clsPrefix = 'cn-ui-m-message';
const ICON_MAP = {
    success: 'complete-r-fill',
    warning: 'exclamation-t-fill',
    error: 'error-r-fill',
    notice: '',
    help: 'exclamation-r-fill',
    loading: '',
};
const TOAST_ICON_MAP = {
    success: 'complete-r',
    warning: 'exclamation-r',
    error: 'error-r',
    notice: '',
    help: 'help',
    loading: '',
};
const Message = (props, ref) => {
    const { className, shape = 'inline', type = 'notice', visible: visibleProp, defaultVisible = true, title, iconType, closable = false, children, zIndex, style, onClose, onClick, ...others } = props;
    const contentRef = useRef();
    const [isOverflow, setOverflow] = useState(false);
    useEffect(() => {
        if (!contentRef.current)
            return;
        if (shape === 'toast' || !children) {
            setOverflow(false);
            return;
        }
        const contentStyle = getComputedStyle(contentRef.current);
        const lineHeight = Number(contentStyle?.lineHeight?.replace('px', '')) || 21;
        const height = Number(contentStyle?.height?.replace('px', ''));
        if (height - 10 > lineHeight * 3) {
            setOverflow(true);
        }
        else {
            setOverflow(false);
        }
    }, [shape, children, className]);
    const handleMessageClick = useCallback(() => {
        if (!isOverflow) {
            return;
        }
        CnDialog.notice({
            title: $i18n.get({ id: 'Hint', dm: '提示', ns: 'CnMessage' }),
            content: children,
            className: `${clsPrefix}-full-notice-wrap`,
            okProps: {
                text: $i18n.get({
                    id: '31255237377527808.CNTM',
                    dm: '知道了',
                    ns: 'CnMessage',
                }),
                type: 'primary',
            },
        });
    }, [isOverflow, children]);
    const newStyle = {
        zIndex,
        ...style,
    };
    const [visible, setVisible, isControlled] = useValue(props, defaultVisible, {
        valueName: 'visible',
    });
    if (!visible) {
        return null;
    }
    const toastContent = title || children;
    const isToast = shape === 'toast';
    const hideIcon = (iconType === null ||
        iconType === '' ||
        iconType === false ||
        (iconType === undefined && !ICON_MAP[type])) &&
        type !== 'loading';
    // const hideIcon = isToast ? hideToastIcon : hideInlineIcon;
    const isSimpleMode = shape === 'inline' && !title && children;
    const handleClose = () => {
        if (!isControlled) {
            setVisible(false);
        }
        if (isFunction(onClose)) {
            onClose();
        }
    };
    const mappedIconType = shape === 'toast' ? TOAST_ICON_MAP[type] : ICON_MAP[type];
    return (React.createElement("div", { ref: ref, className: classNames(clsPrefix, `${clsPrefix}--${type}`, `${clsPrefix}--${shape}`, className, {
            [`${clsPrefix}--${shape}-${type}`]: shape && type,
            [`${clsPrefix}--inline--no-title`]: shape === 'inline' && !toastContent,
            [`${clsPrefix}--toast-with-icon`]: shape === 'toast' && !hideIcon,
        }), style: newStyle, ...others, onClick: handleMessageClick },
        !hideIcon && (React.createElement("div", { className: `${clsPrefix}-icon-container--${shape}` }, type === 'loading' ? (React.createElement(CnLoading, { size: "large" })) : (React.createElement(CnIcon, { type: iconType || mappedIconType || type, outerClassName: classNames(`${clsPrefix}-icon`, `${clsPrefix}-icon--${type}`, `${clsPrefix}-icon--${shape}`) })))),
        React.createElement("div", { className: `${clsPrefix}-container ${clsPrefix}-container--${shape}`, onClick: onClick },
            title && !isToast && (React.createElement("div", { className: classNames(`${clsPrefix}-title`) }, isString(title) ? (React.createElement("span", { className: classNames(`${clsPrefix}-title`, {
                    [`${clsPrefix}-title--${type}`]: isSimpleMode,
                }) }, title || children)) : (title))),
            React.createElement("div", { className: classNames(`${clsPrefix}-content-container`, `${clsPrefix}-content-container--${shape}`) },
                isToast && isString(toastContent) ? (React.createElement("span", { className: classNames(`${clsPrefix}-content`, `${clsPrefix}-content--${shape}`) }, toastContent)) : null,
                isToast && !isString(toastContent) ? toastContent : null,
                !isToast && children ? (React.createElement("div", { className: classNames(`${clsPrefix}-content--inline`, {
                        [`${clsPrefix}-content--no-title`]: shape === 'inline' && !title,
                        [`${clsPrefix}-content--inline-${type}`]: isSimpleMode,
                        [`${clsPrefix}-content--inline-simple`]: isSimpleMode,
                    }) }, children)) : null,
                !isToast && children ? (React.createElement("div", { className: classNames(`${clsPrefix}-content--inline-temp`, {
                        [`${clsPrefix}-content--no-title`]: shape === 'inline' && !title,
                        [`${clsPrefix}-content--inline-${type}`]: isSimpleMode,
                        [`${clsPrefix}-content--inline-simple`]: isSimpleMode,
                    }), 
                    // @ts-ignore
                    ref: contentRef }, children)) : null)),
        isOverflow || (onClick && !isToast) ? (React.createElement(CnIcon, { type: "triangle-right-fill", size: "small", outerClassName: classNames(`${clsPrefix}-action`, `${clsPrefix}-close--${type}`), onClick: onClick })) : null,
        closable && !isToast ? (React.createElement(CnIcon, { type: "close", size: "small", outerClassName: classNames(`${clsPrefix}-close`, {
                [`${clsPrefix}-close--${type}`]: isSimpleMode,
                [`${clsPrefix}-close-after-icon`]: onClick && !isToast,
            }), onClick: (e) => {
                e.stopPropagation();
                handleClose();
            } })) : null));
};
export const CnMessage = forwardRef(Message);
CnMessage.defaultProps = {
    shape: 'inline',
    type: 'notice',
    defaultVisible: true,
    closable: false,
};
CnMessage.displayName = 'CnMessage';
